import { useAuth } from '../context/AuthContext';
import { USER_ROLES } from '../utils/constants';
import { DEFAULT_ROLE_PERMISSIONS } from '../utils/navigationUtils';

/**
 * Custom hook to check granular CRUD permissions
 * @returns {Object} Permission checking functions
 */
export const usePermissions = () => {
  const { user } = useAuth();

  // Get user with permissions from localStorage as fallback
  const getUserWithPermissions = () => {
    if (user && user.granularPermissions) {
      return user;
    }

    // Try to get from localStorage
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && parsedUser.granularPermissions) {
          return parsedUser;
        }
      }
    } catch (e) {
    }

    return user;
  };

  const currentUser = getUserWithPermissions();
  const userRole = currentUser?.role;

  /**
   * Check if user has a specific permission with a specific action
   * @param {string} permissionKey - The permission key (e.g., 'employee_management', 'recruitment')
   * @param {string} action - The CRUD action ('read', 'create', 'update', 'delete')
   * @returns {boolean} True if user has permission
   */
  const hasPermission = (permissionKey, action = 'read') => {
    // Super admin has all permissions
    if (userRole === USER_ROLES.SUPER_ADMIN) {
      return true;
    }

    // Check if explicit permission exists in user's granularPermissions
    if (currentUser && currentUser.granularPermissions && currentUser.granularPermissions[permissionKey] !== undefined) {
      const permission = currentUser.granularPermissions[permissionKey];
      if (!permission) return false;

      if (Array.isArray(action)) {
        return action.some(act => permission[act] === true);
      }
      return permission[action] === true;
    }

    // Fallback to role default permissions
    const defaultPerms = DEFAULT_ROLE_PERMISSIONS[userRole];
    if (defaultPerms && defaultPerms.includes(permissionKey)) {
      if (action === 'read') return true;
      if (action === 'create' || action === 'update') {
        return permissionKey !== 'dashboard' && permissionKey !== 'reports' && permissionKey !== 'analytics';
      }
      if (action === 'delete') {
        return userRole?.endsWith('_manager');
      }
      if (Array.isArray(action)) {
        return action.includes('read');
      }
    }

    return false;
  };

  /**
   * Check if user can read (view) a module
   * @param {string} permissionKey - The permission key
   * @returns {boolean}
   */
  const canRead = (permissionKey) => hasPermission(permissionKey, 'read');

  /**
   * Check if user can create in a module
   * @param {string} permissionKey - The permission key
   * @returns {boolean}
   */
  const canCreate = (permissionKey) => hasPermission(permissionKey, 'create');

  /**
   * Check if user can update in a module
   * @param {string} permissionKey - The permission key
   * @returns {boolean}
   */
  const canUpdate = (permissionKey) => hasPermission(permissionKey, 'update');

  /**
   * Check if user can delete in a module
   * @param {string} permissionKey - The permission key
   * @returns {boolean}
   */
  const canDelete = (permissionKey) => {
    const result = hasPermission(permissionKey, 'delete');
    return result;
  };

  /**
   * Get all permissions for a module
   * @param {string} permissionKey - The permission key
   * @returns {Object} Object with read, create, update, delete booleans
   */
  const getModulePermissions = (permissionKey) => {
    if (userRole === USER_ROLES.SUPER_ADMIN) {
      return { read: true, create: true, update: true, delete: true };
    }

    return {
      read: hasPermission(permissionKey, 'read'),
      create: hasPermission(permissionKey, 'create'),
      update: hasPermission(permissionKey, 'update'),
      delete: hasPermission(permissionKey, 'delete')
    };
  };

  return {
    hasPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    getModulePermissions,
    userRole,
    currentUser
  };
};

export default usePermissions;

